import React, { useState, useEffect } from 'react';
import { School as SchoolIcon } from 'lucide-react';
import { School } from '../../types/School';
import { apiClient } from '../../config/api';
import AdminTable from '../shared/AdminTable';
import AdminHeader from '../shared/AdminHeader';
import SchoolForm from './SchoolForm';
import { schoolTableConfig } from './SchoolTableConfig';

interface SchoolFormData {
  name: string;
  displayName?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
  phone?: string;
  email?: string;
  website?: string;
  isActive: boolean;
}

const EnhancedSchoolManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingSchool, setEditingSchool] = useState<School | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load schools and populate dynamic filter options (same pattern as other admin components)
  useEffect(() => {
    const loadSchools = async () => {
      try {
        // Get the single school data for filter options
        const schoolData = await apiClient.get('/api/public/school');

        // Update state filter options dynamically (same as other components)
        if (schoolData?.address?.state) {
          schoolTableConfig.filterOptions.state = [{
            value: schoolData.address.state,
            label: schoolData.address.state
          }];
        }
      } catch (error) {
        console.error('Failed to load schools:', error);
      }
    };

    loadSchools();
  }, [refreshTrigger]);

  // Actions object (same pattern as other admin components)
  const actions = {
    handleCreate: () => {
      setEditingSchool(undefined);
      setShowForm(true);
    },

    handleEdit: (school: School) => {
      setEditingSchool(school);
      setShowForm(true);
    },

    handleView: (school: School) => {
      setEditingSchool(school);
      setShowForm(true);
    },

    handleDelete: async (schoolId: string) => {
      if (!window.confirm('Are you sure you want to delete this school?')) {
        return;
      }

      try {
        setIsLoading(true);
        // Use direct API call to match other components
        await apiClient.delete(`/api/admin/schools/${schoolId}`);
        setRefreshTrigger(prev => prev + 1);
      } catch (error) {
        console.error('Failed to delete school:', error);
        alert('Failed to delete school. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },

    handleFormSubmit: async (formData: SchoolFormData) => {
      try {
        setIsLoading(true);

        if (editingSchool) {
          // Update existing school
          await apiClient.put(`/api/admin/schools/${editingSchool.id}`, formData);
        } else {
          // Create new school
          await apiClient.post('/api/admin/schools', formData);
        }

        setShowForm(false);
        setEditingSchool(undefined);
        setRefreshTrigger(prev => prev + 1);
      } catch (error) {
        console.error('Failed to save school:', error);
        throw error; // Let the form handle the error display
      } finally {
        setIsLoading(false);
      }
    },

    handleFormCancel: () => {
      setShowForm(false);
      setEditingSchool(undefined);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8 space-y-6">
        {/* Header */}
        <AdminHeader
          title="Manage Schools"
          icon={<SchoolIcon size={32} />}
          subtitle="Configure and manage organization settings and information"
        />

        <div className="flex justify-end">
          <button
            onClick={actions.handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <SchoolIcon size={20} />
            Add New School
          </button>
        </div>

        {/* AdminTable - exact same component as other admin pages */}
        <AdminTable
          config={schoolTableConfig}
          onView={actions.handleView}
          onEdit={actions.handleEdit}
          onDelete={actions.handleDelete}
          isLoading={isLoading}
          refreshTrigger={refreshTrigger}
        />

        {/* Form Modal - same pattern as other admin components */}
        {showForm && (
          <SchoolForm
            item={editingSchool}
            isOpen={showForm}
            onSubmit={actions.handleFormSubmit}
            onClose={actions.handleFormCancel}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default EnhancedSchoolManagement;
